/**
 * Centralized Router Configuration
 * Bulletproof React Pattern - Task 32.8
 *
 * Architecture:
 * - Single ProtectedRoute boundary for authentication
 * - RoleGuard at route-group level for authorization (3 instances)
 * - Native React Router v6.4+ lazy loading pattern
 * - Clean hierarchical route structure
 *
 * Route Organization:
 * - Public routes (22 routes)
 * - Protected routes with single auth boundary
 *   - Shared settings (6 routes)
 *   - Onboarding (4 routes) - auth required
 *   - Learner routes (27 routes) - RoleGuard (includes learning-hub, skills, portfolio)
 *   - Employer routes (9 routes) - RoleGuard
 *   - Admin routes (13 routes) - RoleGuard
 *
 * Public Learning Hub Hierarchy (Task 153.14):
 * - /learning-hub (landing page)
 *   - /learning-hub/workshops (catalogue)
 *   - /learning-hub/resource-library
 *   - /learning-hub/resource-library/:resourceId (detail)
 *   - /learning-hub/resource-library/tools (tools index)
 *
 * Serene Zone Routes (outside PublicLayout for immersive experience):
 * - /learning-hub/resource-library/tools/breathing (SereneZoneLayout)
 *   - /learning-hub/resource-library/tools/breathing/478 (4-7-8)
 *   - /learning-hub/resource-library/tools/breathing/box (box breathing)
 *   - /learning-hub/resource-library/tools/breathing/grounding (grounding)
 *
 * Learner Route Hierarchy (Task 83.12, Task 183.12):
 * - /learner/dashboard
 * - /learner/learning-hub (landing page)
 *   - /learner/learning-hub/workshops (catalogue)
 *     - /learner/learning-hub/workshops/:id (detail - accepts slug or UUID)
 *     - /learner/learning-hub/workshops/:id/resource/:resourceSlug (SEO-friendly)
 *   - /learner/learning-hub/resource-library
 * - /learner/progress (Progress Tracker)
 * - /learner/skills (Skills feature)
 * - /learner/analytics (Learning Analytics)
 * - /learner/portfolio/...
 *
 * All routes use the native .lazy() pattern for optimal code splitting.
 * Route components are defined in src/app/routes/ with Component exports.
 */

import { useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { Home } from 'lucide-react';
import { ProtectedRoute } from '@/features/auth/components/protected-route';
import { RoleGuard } from '@/components/guards/role-guard';
import { PublicLayout } from '@/components/layouts/public-layout';
import { AppLayout } from '@/components/layouts/app-layout';
import { OnboardingRouteLayout } from '@/components/layouts/onboarding-route-layout';
import { SereneZoneLayout } from '@/features/serene-zone/components/serene-zone-layout';
import { RouteErrorFallback } from '@/components/errors/error-fallback';
import { BreadcrumbProvider } from '@/providers/breadcrumb-provider';
import type { BreadcrumbHandle } from '@/lib/breadcrumb-types';

/**
 * Loading fallback for route hydration
 * Shown while lazy-loaded route modules are being fetched
 */
function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  // ═══════════════════════════════════════════════════════════════
  // SERENE ZONE ROUTES (immersive, outside PublicLayout)
  // ═══════════════════════════════════════════════════════════════
  {
    path: '/learning-hub/resource-library/tools/breathing',
    element: <SereneZoneLayout />,
    errorElement: <RouteErrorFallback />,
    hydrateFallbackElement: <RouteLoadingFallback />,
    children: [
      {
        index: true,
        lazy: () => import('./routes/public/tools/breathing/index'),
      },
      {
        path: '478',
        lazy: () => import('./routes/public/tools/breathing/four-seven-eight'),
      },
      {
        path: 'box',
        lazy: () => import('./routes/public/tools/breathing/box'),
      },
      {
        path: 'grounding',
        lazy: () => import('./routes/public/tools/breathing/grounding'),
      },
      // Catch-all for invalid breathing technique paths
      {
        path: '*',
        lazy: async () => ({
          Component: function InvalidBreathingRedirect() {
            const navigate = useNavigate();
            useEffect(() => {
              navigate(ROUTES.PUBLIC_TOOLS_BREATHING, { replace: true });
            }, [navigate]);
            return null;
          },
        }),
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // PUBLIC ROUTES
  // ═══════════════════════════════════════════════════════════════
  {
    element: <PublicLayout />,
    errorElement: <RouteErrorFallback />,
    hydrateFallbackElement: <RouteLoadingFallback />,
    children: [
      // Landing & Marketing
      { path: '/', lazy: () => import('./routes/landing') },
      { path: '/employment-journey', lazy: () => import('./routes/marketing/employment-journey') },
      { path: '/for-employers', lazy: () => import('./routes/marketing/for-employers') },

      // Public Learning Hub (unauthenticated - Task 83.18, Task 153.10)
      {
        path: '/learning-hub',
        handle: { breadcrumb: { label: 'Learning Hub' } } satisfies BreadcrumbHandle,
        children: [
          {
            index: true,
            lazy: () => import('./routes/public/learning-hub/index'),
          },
          {
            path: 'workshops',
            lazy: () => import('./routes/public/learning-hub/workshops/index'),
          },
          {
            path: 'resource-library',
            handle: { breadcrumb: { label: 'Resource Library' } } satisfies BreadcrumbHandle,
            children: [
              {
                index: true,
                lazy: () => import('./routes/public/learning-hub/resource-library/index'),
              },
              {
                path: ':slug',
                lazy: () => import('./routes/public/learning-hub/resource-library/[slug]'),
              },
            ],
          },
        ],
      },

      // Network
      {
        path: '/network',
        children: [
          { index: true, element: <Navigate to="/network/companies" replace /> },
          {
            path: 'companies',
            lazy: () => import('./routes/public/network/index'),
            handle: { breadcrumb: { label: 'Companies' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'companies/:slug',
            lazy: () => import('./routes/public/network/[slug]'),
            handle: { breadcrumb: { label: 'Company' } } satisfies BreadcrumbHandle,
          },
        ],
      },

      // Shared Document (public view - supports CV and Cover Letter)
      { path: '/cv/:cvId', lazy: () => import('./routes/public/shared-document') },
      { path: '/cover-letter/:token', lazy: () => import('./routes/public/shared-document') },

      // Auth Routes
      { path: '/auth/sign-in', lazy: () => import('./routes/auth/sign-in') },
      { path: '/auth/sign-up', lazy: () => import('./routes/auth/sign-up') },
      { path: '/auth/callback', lazy: () => import('./routes/auth/callback') },
      { path: '/auth/account-type', lazy: () => import('./routes/auth/account-type-selection') },
      { path: '/auth/employer-sign-up', lazy: () => import('./routes/auth/employer-sign-up') },
      { path: '/auth/join-team', lazy: () => import('./routes/auth/join-team') },
      { path: '/auth/verify-email', lazy: () => import('./routes/auth/verify-email') },
      { path: '/auth/forgot-password', lazy: () => import('./routes/auth/forgot-password') },
      { path: '/reset-password', lazy: () => import('./routes/auth/reset-password') },

      // Legal Pages
      { path: '/terms', lazy: () => import('./routes/legal/terms') },
      { path: '/privacy', lazy: () => import('./routes/legal/privacy') },
      { path: '/cookies', lazy: () => import('./routes/legal/cookies') },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // PROTECTED ROUTES - SINGLE AUTH BOUNDARY
  // ═══════════════════════════════════════════════════════════════
  {
    element: (
      <ProtectedRoute>
        <Outlet />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorFallback />,
    hydrateFallbackElement: <RouteLoadingFallback />,
    children: [
      // ─────────────────────────────────────────────────────────────
      // ONBOARDING (protected but OUTSIDE AppLayout - no sidebar/header)
      // Users must complete onboarding before accessing main app
      // ─────────────────────────────────────────────────────────────
      {
        path: '/get-started',
        element: <OnboardingRouteLayout />,
        children: [
          { index: true, lazy: () => import('./routes/onboarding/get-started') },
          { path: 'about', lazy: () => import('./routes/onboarding/about-you') },
          { path: 'goals', lazy: () => import('./routes/onboarding/your-goals') },
          { path: 'preferences', lazy: () => import('./routes/onboarding/learning-preferences') },
        ],
      },

      // ─────────────────────────────────────────────────────────────
      // MAIN APP ROUTES (with AppLayout - sidebar/header visible)
      // ─────────────────────────────────────────────────────────────
      {
        element: <AppLayout />,
        children: [
          // Smart Dashboard Redirect
          { path: '/dashboard', lazy: () => import('./routes/dashboard') },

          // SHARED SETTINGS (all authenticated users)
          {
            path: '/settings',
            handle: { breadcrumb: { label: 'Settings' } } satisfies BreadcrumbHandle,
            children: [
              { index: true, element: <Navigate to="/settings/profile" replace /> },
              {
                path: 'profile',
                lazy: () => import('./routes/settings/profile'),
                handle: { breadcrumb: { label: 'Profile' } } satisfies BreadcrumbHandle,
              },
              {
                path: 'security',
                lazy: () => import('./routes/settings/security'),
                handle: { breadcrumb: { label: 'Security' } } satisfies BreadcrumbHandle,
              },
              {
                path: 'accessibility',
                lazy: () => import('./routes/settings/accessibility'),
                handle: { breadcrumb: { label: 'Accessibility' } } satisfies BreadcrumbHandle,
              },
              {
                path: 'privacy',
                lazy: () => import('./routes/settings/privacy'),
                handle: { breadcrumb: { label: 'Privacy' } } satisfies BreadcrumbHandle,
              },
              {
                path: 'notifications',
                lazy: () => import('./routes/settings/notifications'),
                handle: { breadcrumb: { label: 'Notifications' } } satisfies BreadcrumbHandle,
              },
              {
                path: 'cookies',
                lazy: () => import('./routes/settings/cookies'),
                handle: { breadcrumb: { label: 'Cookie Preferences' } } satisfies BreadcrumbHandle,
              },
            ],
          },

      // ─────────────────────────────────────────────────────────────
      // LEARNER ROUTES
      // ─────────────────────────────────────────────────────────────
      {
        path: '/learner',
        element: <RoleGuard allowedTypes={['learner']} />,
        handle: { breadcrumb: { label: 'Home', icon: Home } } satisfies BreadcrumbHandle,
        children: [
          { index: true, element: <Navigate to="/learner/dashboard" replace /> },
          {
            path: 'dashboard',
            lazy: () => import('./routes/learner/dashboard/index'),
            handle: { breadcrumb: { label: 'Dashboard' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'learning-hub',
            handle: { breadcrumb: { label: 'Learning Hub' } } satisfies BreadcrumbHandle,
            children: [
              {
                index: true,
                lazy: () => import('./routes/learner/learning-hub/index'),
              },
              {
                path: 'workshops',
                handle: { breadcrumb: { label: 'Workshops' } } satisfies BreadcrumbHandle,
                children: [
                  {
                    index: true,
                    lazy: () => import('./routes/learner/learning-hub/workshops/index'),
                  },
                  {
                    path: ':id',
                    handle: { breadcrumb: { label: 'Workshop' }, supportsFocusMode: true } satisfies BreadcrumbHandle,
                    children: [
                      {
                        index: true,
                        lazy: () => import('./routes/learner/learning-hub/workshops/[id]'),
                      },
                      {
                        path: 'resource/:resourceSlug',
                        lazy: () => import('./routes/learner/learning-hub/workshops/[slug]/resource/[resourceSlug]'),
                        handle: { breadcrumb: { label: 'Resource' } } satisfies BreadcrumbHandle,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'resource-library',
                handle: { breadcrumb: { label: 'Resource Library' } } satisfies BreadcrumbHandle,
                children: [
                  {
                    index: true,
                    lazy: () => import('./routes/learner/learning-hub/resource-library/index'),
                  },
                  {
                    path: ':slug',
                    lazy: () => import('./routes/learner/learning-hub/resource-library/[slug]'),
                  },
                ],
              },
            ],
          },
          {
            path: 'progress',
            lazy: () => import('./routes/learner/progress'),
            handle: { breadcrumb: { label: 'Progress Tracker' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'skills',
            lazy: () => import('./routes/learner/skills'),
            handle: { breadcrumb: { label: 'Skills' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'analytics',
            lazy: () => import('./routes/learner/analytics'),
            handle: { breadcrumb: { label: 'Learning Analytics' } } satisfies BreadcrumbHandle,
          },

          {
            path: 'network',
            handle: { breadcrumb: { label: 'Network' } } satisfies BreadcrumbHandle,
            children: [
              { index: true, element: <Navigate to="companies" replace /> },
              {
                path: 'companies',
                lazy: () => import('./routes/learner/network/index'),
                handle: { breadcrumb: { label: 'Companies' } } satisfies BreadcrumbHandle,
              },
              {
                path: 'companies/:slug',
                lazy: () => import('./routes/learner/network/[slug]'),
                handle: { breadcrumb: { label: 'Company' } } satisfies BreadcrumbHandle,
              },
            ],
          },

          {
            path: 'portfolio',
            handle: { breadcrumb: { label: 'My Portfolio' } } satisfies BreadcrumbHandle,
            children: [
              { index: true, lazy: () => import('./routes/learner/portfolio/index') },
              {
                path: 'files',
                lazy: () => import('./routes/learner/portfolio/files'),
                handle: { breadcrumb: { label: 'Files' } } satisfies BreadcrumbHandle,
              },
              {
                path: 'bookmarks',
                lazy: () => import('./routes/learner/portfolio/bookmarks/index'),
                handle: { breadcrumb: { label: 'Bookmarks' } } satisfies BreadcrumbHandle,
              },

              /**
               * CV Builder Routes (Multi-CV Workflow)
               *
               * Route Structure:
               * - /learner/portfolio/cv        → Redirects to list (index)
               * - /learner/portfolio/cv/list   → Displays all user's CVs
               * - /learner/portfolio/cv/new    → Create new CV workflow
               * - /learner/portfolio/cv/:cvId  → Edit specific CV (main builder)
               * - /learner/portfolio/cv/:cvId/:section → Edit individual CV section (future)
               *
               * Route Parameters:
               * - :cvId - UUID format required (e.g., 550e8400-e29b-41d4-a716-446655440000)
               *
               * UUID Validation:
               * Validation for :cvId is handled in cv-builder.tsx (not at route level)
               * to provide user-friendly redirect to CV list on invalid/malformed IDs.
               * This prevents "invalid input syntax for type uuid" database errors.
               *
               * Pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
               */
              {
                path: 'cv',
                handle: { breadcrumb: { label: 'CVs' } } satisfies BreadcrumbHandle,
                children: [
                  { index: true, lazy: () => import('./routes/learner/portfolio/cv/list') },
                  {
                    path: 'new',
                    lazy: () => import('./routes/learner/portfolio/cv/new'),
                    handle: { breadcrumb: { label: 'New CV' } } satisfies BreadcrumbHandle,
                  },
                  {
                    path: ':cvId',
                    lazy: () => import('./routes/learner/portfolio/cv/cv-builder'),
                    handle: { breadcrumb: { label: 'CV' } } satisfies BreadcrumbHandle,
                  },
                  { path: ':cvId/:section', lazy: () => import('./routes/learner/portfolio/cv/cv-section') },
                ],
              },

              {
                path: 'cover-letters',
                handle: { breadcrumb: { label: 'Cover Letters' } } satisfies BreadcrumbHandle,
                children: [
                  { index: true, lazy: () => import('./routes/learner/portfolio/cover-letters/list') },
                  { path: 'new', lazy: () => import('./routes/learner/portfolio/cover-letters/new') },
                  { path: ':letterId', lazy: () => import('./routes/learner/portfolio/cover-letters/cover-letter-editor') },
                ],
              },

              {
                path: 'notes',
                handle: { breadcrumb: { label: 'Notes' } } satisfies BreadcrumbHandle,
                children: [
                  { index: true, lazy: () => import('./routes/learner/portfolio/notes/list') },
                  { path: 'new', lazy: () => import('./routes/learner/portfolio/notes/new') },
                  { path: ':noteId', lazy: () => import('./routes/learner/portfolio/notes/note-editor') },
                ],
              },

              {
                path: 'email-templates',
                handle: { breadcrumb: { label: 'Email Templates' } } satisfies BreadcrumbHandle,
                children: [
                  { index: true, lazy: () => import('./routes/learner/portfolio/email-templates/list') },
                  { path: 'new', lazy: () => import('./routes/learner/portfolio/email-templates/new') },
                  { path: ':templateId', lazy: () => import('./routes/learner/portfolio/email-templates/email-template-editor') },
                ],
              },

              {
                path: 'career-planner',
                handle: { breadcrumb: { label: 'Career Planner' } } satisfies BreadcrumbHandle,
                children: [
                  { index: true, lazy: () => import('./routes/learner/portfolio/career-planner/list') },
                  { path: 'new', lazy: () => import('./routes/learner/portfolio/career-planner/new') },
                  {
                    path: ':goalId',
                    lazy: () => import('./routes/learner/portfolio/career-planner/goal-editor'),
                    handle: { breadcrumb: { label: 'Goal Details' } } satisfies BreadcrumbHandle,
                  },
                ],
              },

              {
                path: 'interview-prep',
                lazy: () => import('./routes/learner/portfolio/interview-prep/index'),
                handle: { breadcrumb: { label: 'Interview Prep' } } satisfies BreadcrumbHandle,
              },
            ],
          },

          // Legacy Tools Routes - Redirect to public routes (Task 156.23)
          // These routes are deprecated; breathing exercises are now public at /learning-hub/resource-library/tools/breathing/*
          {
            path: 'tools/breathing/*',
            lazy: async () => ({
              Component: function LegacyBreathingRedirect() {
                const navigate = useNavigate();
                const location = window.location;
                useEffect(() => {
                  // Map old paths to new public paths
                  const oldPath = location.pathname;
                  const newPath = oldPath.replace('/learner/tools/breathing', '/learning-hub/resource-library/tools/breathing');
                  navigate(newPath, { replace: true });
                }, [navigate, location.pathname]);
                return null;
              },
            }),
          },
        ],
      },

      // ─────────────────────────────────────────────────────────────
      // EMPLOYER ROUTES
      // ─────────────────────────────────────────────────────────────
      {
        path: '/employer',
        element: <RoleGuard allowedTypes={['employer']} />,
        handle: { breadcrumb: { label: 'Employer', hidden: true } } satisfies BreadcrumbHandle,
        children: [
          {
            path: 'dashboard',
            lazy: () => import('./routes/network/employer/dashboard/index'),
            handle: { breadcrumb: { label: 'Dashboard', icon: Home } } satisfies BreadcrumbHandle,
          },
          {
            path: 'network',
            lazy: () => import('./routes/network/employer/network'),
            handle: { breadcrumb: { label: 'Network' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'analytics',
            lazy: () => import('./routes/network/employer/dashboard/analytics'),
            handle: { breadcrumb: { label: 'Analytics' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'company',
            handle: { breadcrumb: { label: 'Company Profile' } } satisfies BreadcrumbHandle,
            children: [
              { index: true, lazy: () => import('./routes/network/employer/dashboard/company/index') },
              { path: 'create', lazy: () => import('./routes/network/employer/dashboard/company/create') },
              { path: 'pending', lazy: () => import('./routes/network/employer/dashboard/company/pending') },
            ],
          },
          {
            path: 'team',
            element: <RoleGuard allowedTypes={['employer']} requirePrimary />,
            handle: { breadcrumb: { label: 'Team Management' } } satisfies BreadcrumbHandle,
            children: [
              { index: true, lazy: () => import('./routes/network/employer/dashboard/team') },
            ],
          },
        ],
      },

      // ─────────────────────────────────────────────────────────────
      // ADMIN ROUTES
      // ─────────────────────────────────────────────────────────────
      {
        path: '/admin',
        element: <RoleGuard allowedTypes={['platform']} />,
        handle: { breadcrumb: { label: 'Admin', hidden: true } } satisfies BreadcrumbHandle,
        children: [
          {
            path: 'dashboard',
            lazy: () => import('./routes/admin/dashboard/index'),
            handle: { breadcrumb: { label: 'Dashboard', icon: Home } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/users/setup',
            lazy: () => import('./routes/admin/dashboard/users/setup'),
            handle: { breadcrumb: { label: 'Setup' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/email',
            lazy: () => import('./routes/admin/dashboard/email/index'),
            handle: { breadcrumb: { label: 'Email Templates' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/analytics',
            lazy: () => import('./routes/admin/dashboard/analytics/index'),
            handle: { breadcrumb: { label: 'Analytics' } } satisfies BreadcrumbHandle,
          },

          // Reports (admin/dashboard/reports)
          {
            path: 'dashboard/reports',
            lazy: () => import('./routes/admin/dashboard/reports/index'),
            handle: { breadcrumb: { label: 'Reports' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/reports/workshop-performance',
            lazy: () => import('./routes/admin/dashboard/reports/workshop-performance'),
            handle: { breadcrumb: { label: 'Workshop Performance' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/reports/content-usage',
            lazy: () => import('./routes/admin/dashboard/reports/content-usage'),
            handle: { breadcrumb: { label: 'Content Usage' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/reports/registration',
            lazy: () => import('./routes/admin/dashboard/reports/registration'),
            handle: { breadcrumb: { label: 'Registration Report' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/reports/user-activity',
            lazy: () => import('./routes/admin/dashboard/reports/user-activity'),
            handle: { breadcrumb: { label: 'User Activity' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/reports/onboarding-completion',
            lazy: () => import('./routes/admin/dashboard/reports/onboarding-completion'),
            handle: { breadcrumb: { label: 'Onboarding Completion' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/reports/age-breakdown',
            lazy: () => import('./routes/admin/dashboard/reports/age-breakdown'),
            handle: { breadcrumb: { label: 'Age Breakdown' } } satisfies BreadcrumbHandle,
          },

          // Workshop Management (admin/dashboard/workshops)
          {
            path: 'dashboard/workshops',
            lazy: () => import('./routes/admin/dashboard/workshops/index'),
            handle: { breadcrumb: { label: 'Workshops' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/workshops/new',
            lazy: () => import('./routes/admin/dashboard/workshops/new'),
            handle: { breadcrumb: { label: 'New Workshop' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/workshops/:id',
            lazy: () => import('./routes/admin/dashboard/workshops/[id]/index'),
            handle: { breadcrumb: { label: 'Workshop' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/workshops/:id/edit',
            lazy: () => import('./routes/admin/dashboard/workshops/[id]/edit'),
            handle: { breadcrumb: { label: 'Edit Workshop' } } satisfies BreadcrumbHandle,
          },

          // Content Management (admin/dashboard/content)
          {
            path: 'dashboard/content',
            lazy: () => import('./routes/admin/dashboard/content/index'),
            handle: { breadcrumb: { label: 'Content' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/content/new',
            lazy: () => import('./routes/admin/dashboard/content/new'),
            handle: { breadcrumb: { label: 'New Content' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/content/upload',
            lazy: () => import('./routes/admin/dashboard/content/upload'),
            handle: { breadcrumb: { label: 'Upload' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/content/:id',
            lazy: () => import('./routes/admin/dashboard/content/[id]/index'),
            handle: { breadcrumb: { label: 'Content Details' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/content/:id/edit',
            lazy: () => import('./routes/admin/dashboard/content/[id]/edit'),
            handle: { breadcrumb: { label: 'Edit Content' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/content/:id/versions',
            lazy: () => import('./routes/admin/dashboard/content/[id]/versions'),
            handle: { breadcrumb: { label: 'Version History' } } satisfies BreadcrumbHandle,
          },

          {
            path: 'dashboard/companies',
            lazy: () => import('./routes/admin/dashboard/network/companies/index'),
            handle: { breadcrumb: { label: 'Employer Management' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/users',
            lazy: () => import('./routes/admin/dashboard/users/index'),
            handle: { breadcrumb: { label: 'Users' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/roles',
            element: <RoleGuard allowedTypes={['platform']} requiredPermission="admin:manage" />,
            handle: { breadcrumb: { label: 'Roles' } } satisfies BreadcrumbHandle,
            children: [
              { index: true, lazy: () => import('./routes/admin/dashboard/roles/index') },
            ],
          },

          // Training & Documentation (admin/dashboard/training)
          {
            path: 'dashboard/training',
            lazy: () => import('./routes/admin/dashboard/training/index'),
            handle: { breadcrumb: { label: 'Training' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/training/sitemap-flowcharts',
            lazy: () => import('./routes/admin/dashboard/training/sitemap-flowcharts'),
            handle: { breadcrumb: { label: 'Sitemap Flowcharts' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/training/content-management-guide',
            lazy: () => import('./routes/admin/dashboard/training/content-management-guide'),
            handle: { breadcrumb: { label: 'Content Guide' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/training/reporting-guide',
            lazy: () => import('./routes/admin/dashboard/training/reporting-guide'),
            handle: { breadcrumb: { label: 'Reporting Guide' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/training/email-management',
            lazy: () => import('./routes/admin/dashboard/training/email-management'),
            handle: { breadcrumb: { label: 'Email Guide' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/training/company-employer-management',
            lazy: () => import('./routes/admin/dashboard/training/company-employer-management'),
            handle: { breadcrumb: { label: 'Company & Employer Guide' } } satisfies BreadcrumbHandle,
          },
          {
            path: 'dashboard/training/workshop-management',
            lazy: () => import('./routes/admin/dashboard/training/workshop-management'),
            handle: { breadcrumb: { label: 'Workshop Guide' } } satisfies BreadcrumbHandle,
          },
        ],
      },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // CATCH-ALL (404)
  // ═══════════════════════════════════════════════════════════════
  {
    path: '*',
    lazy: () => import('./routes/public/not-found'),
    hydrateFallbackElement: <RouteLoadingFallback />,
  },
]);

/**
 * Main router component
 * Wraps RouterProvider with BreadcrumbProvider inside router context.
 *
 * IMPORTANT: BreadcrumbProvider MUST be inside RouterProvider because
 * useBreadcrumbs() uses useMatches() and useLocation() which require
 * router context to function properly.
 */
export function AppRouter() {
  return (
    <BreadcrumbProvider>
      <RouterProvider router={router} />
    </BreadcrumbProvider>
  );
}
